import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const EMPTY = { code: '', name: '', budgetAmount: '0', currency: 'CHF', parentId: '', isActive: true, notes: '' };

export function CostCenterModal({ open, onClose, editing }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const costCenters = useQuery({ queryKey: ['finance', 'cost-centers'], queryFn: () => financeApi.listCostCenters().then((r) => r.items), enabled: open });

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...EMPTY, ...editing, budgetAmount: String(editing.budgetAmount ?? '0') } : EMPTY);
      setError(null);
    }
  }, [open, editing]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = useMutation({
    mutationFn: (body) =>
      editing ? financeApi.updateCostCenter(editing.id, body) : financeApi.createCostCenter(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'cost-centers'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'cost-center-report'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (editing) {
      save.mutate({ name: form.name, budgetAmount: form.budgetAmount, isActive: form.isActive, notes: form.notes || null });
    } else {
      save.mutate({ code: form.code, name: form.name, budgetAmount: form.budgetAmount, currency: form.currency, parentId: form.parentId || null, notes: form.notes || null });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? t('cc.modal.editTitle') : t('cc.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('cc.field.code')}</span>
            <input className="input" value={form.code} onChange={set('code')} disabled={Boolean(editing)} required />
          </label>
          <label className="field">
            <span className="field-label">{t('cc.field.currency')}</span>
            <input className="input" maxLength={3} value={form.currency} onChange={set('currency')} disabled={Boolean(editing)} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('cc.field.name')}</span>
          <input className="input" value={form.name} onChange={set('name')} required />
        </label>
        <label className="field">
          <span className="field-label">{t('cc.field.budget')}</span>
          <input className="input" type="number" min="0" step="0.01" value={form.budgetAmount} onChange={set('budgetAmount')} />
        </label>
        {!editing && (
          <label className="field">
            <span className="field-label">{t('cc.field.parent')}</span>
            <select className="input" value={form.parentId} onChange={set('parentId')}>
              <option value="">{t('cc.field.noParent')}</option>
              {(costCenters.data || []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </select>
          </label>
        )}
        {editing && (
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
            <span>{t('cc.field.active')}</span>
          </label>
        )}
        <label className="field">
          <span className="field-label">{t('cc.field.notes')}</span>
          <input className="input" value={form.notes} onChange={set('notes')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
